import {
  IControl,
  IFormDocument,
  IListOptionList,
  IMListRoot,
  IMessageList,
  ISelectOptionList,
} from 'app/services/api5-service/api.interface';
import { clone } from 'ramda';

import {
  getTypedControls,
  getTypedControlsMap,
  getTypedForm,
} from 'common/dynamic-form/utils/form-preparing';

/**
 * Override lists
 *
 * @param prev
 * @param next
 */
function overrideLists(
  prev: IListOptionList[] | undefined,
  next: IListOptionList[],
): IListOptionList[] {
  if (!prev) {
    return next;
  }

  const overrided = [...prev];
  next.forEach(list => {
    const alredyExistedIndex = overrided.findIndex(s => s.$name === list.$name);
    if (alredyExistedIndex !== -1) {
      overrided[alredyExistedIndex] = list;
    } else {
      overrided.push(list);
    }
  });
  return overrided;
}

/**
 * Override slists
 *
 * @param prev
 * @param next
 */
function overrideSlists(
  prev: ISelectOptionList[] | undefined,
  next: ISelectOptionList[],
): ISelectOptionList[] {
  if (!prev) {
    return next;
  }

  const overrided = [...prev];
  next.forEach(slist => {
    const alredyExistedIndex = overrided.findIndex(
      s => s.$name === slist.$name,
    );
    if (alredyExistedIndex !== -1) {
      overrided[alredyExistedIndex] = slist;
    } else {
      overrided.push(slist);
    }
  });
  return overrided;
}

/**
 * Override mlists
 *
 * @param prev
 * @param next
 */
function overrideMlists(
  prev: IMListRoot[] | undefined,
  next: IMListRoot[],
): IMListRoot[] {
  if (!prev) {
    return next;
  }

  const overrides = [...prev];
  next.forEach(mlist => {
    const oldMlistIndex = overrides.findIndex(m => m.$name === mlist.$name);
    if (oldMlistIndex !== -1) {
      const oldMessagesIds = (overrides[oldMlistIndex].message || []).map(
        m => m.$id,
      );
      const uniqueMessages = (mlist.message || []).filter(
        m => !oldMessagesIds.includes(m.$id),
      );
      overrides[oldMlistIndex].message = [
        ...(overrides[oldMlistIndex].message || []),
        ...uniqueMessages,
      ];
    } else {
      overrides.push(mlist);
    }
  });
  return overrides;
}

/**
 * Merge two docs messages, form model, option lists
 *
 * @param prev
 * @param next
 */
function overrideMessages(
  prev: IMessageList | undefined,
  next: IMessageList,
): IMessageList {
  if (!prev) {
    return next;
  }

  return {
    ...next,
    msg: {
      ...prev.msg,
      ...next.msg,
    },
  };
}

/**
 * Override metadata in prev doc, by metadata from next doc
 *
 * @param prev
 * @param next
 */
function overrideMetadata(prev: IFormDocument, next: IFormDocument): void {
  // @TODO i.ablov maybe just use metadata from next doc?

  const prevTypedForm = getTypedForm(prev);
  const nextTypedForm = getTypedForm(next);

  if (!prevTypedForm || !nextTypedForm) {
    return;
  }

  const prevControlsMap = getTypedControlsMap(prevTypedForm);
  const nextControls = getTypedControls(nextTypedForm);
  const nextControlsMetadata = nextControls
    .map(typedControl => typedControl.control.$name)
    .map<[string, Partial<IControl>]>(name => [name, next[name]])
    .filter(([_, metadata]) => metadata);

  nextControlsMetadata.forEach(([name, metadata]) => {
    if (!prevControlsMap[name]) {
      return;
    }

    const control = prevControlsMap[name].control as IControl;
    if (metadata.$readonly) {
      control.$readonly = metadata.$readonly;
    }

    // @TODO i.ablov add slider metadata change by set values
  });
}

/**
 * Merge two docs data
 *
 * @param docA
 * @param docB
 */
export function mergeDocs(
  docA: IFormDocument,
  docB: IFormDocument,
): IFormDocument {
  const newDoc = clone(docA);

  if (docB.slist) {
    newDoc.slist = overrideSlists(docA.slist, docB.slist);
  }
  if (docB.mlist) {
    newDoc.mlist = overrideMlists(docA.mlist, docB.mlist);
  }
  if (docB.list) {
    newDoc.list = overrideLists(docA.list, docB.list);
  }
  if (docB.buttons?.button) {
    newDoc.metadata!.form!.buttons = {
      button: docB.buttons.button,
    };
  }
  if (docB.messages) {
    newDoc.messages = overrideMessages(docA.messages, docB.messages);
  }

  overrideMetadata(newDoc, docB);

  return newDoc;
}
